import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { orderSuggestionsByDocument } from './orderSuggestionsByDocument.ts';
import { fetchLatestDraftAdf, fetchPublishedPageSuggestions } from './publishedSuggestionsApi.ts';

export type PublishedSuggestionsStatus = 'idle' | 'loading' | 'loaded' | 'error';

export type PublishedSuggestionsState = {
	contentId?: string;
	status: PublishedSuggestionsStatus;
	suggestions: Suggestion[];
	selectedSuggestionId?: string;
	shouldStartReviewInEditMode?: boolean;
};

const initialState: PublishedSuggestionsState = {
	status: 'idle',
	suggestions: [],
};

export const PUBLISHED_SUGGESTIONS_CACHE_TTL_MS: number = 2 * 60 * 1000;

type CachedSuggestions = {
	contentAri: string;
	contentId: string;
	suggestions: Suggestion[];
	loadedAt: number;
};

type SuggestionsLoad = {
	contentAri: string;
	contentId: string;
	controller: AbortController;
	promise: Promise<Suggestion[]>;
};

let snapshot: PublishedSuggestionsState = initialState;
let cache: CachedSuggestions | undefined;
// The request in flight, shared by the panel and prefetches for the same content.
let inFlightLoad: SuggestionsLoad | undefined;
// The load the panel is waiting on. Responses for any other load leave the panel alone.
let panelLoad: Promise<Suggestion[]> | undefined;

const getFreshCachedSuggestions = (contentAri: string) =>
	cache &&
	cache.contentAri === contentAri &&
	Date.now() - cache.loadedAt < PUBLISHED_SUGGESTIONS_CACHE_TTL_MS
		? cache.suggestions
		: undefined;
const listeners = new Set<() => void>();

const notifyListeners = () => {
	listeners.forEach((listener) => listener());
};

const setSnapshot = (next: PublishedSuggestionsState) => {
	snapshot = next;
	notifyListeners();
};

const matchSuggestionsToDraft = (
	fetchedSuggestions: Suggestion[],
	latestDraftAdf: Awaited<ReturnType<typeof fetchLatestDraftAdf>> | undefined,
): Suggestion[] => {
	if (!latestDraftAdf) {
		return fetchedSuggestions;
	}
	try {
		return orderSuggestionsByDocument(fetchedSuggestions, latestDraftAdf);
	} catch {
		return fetchedSuggestions;
	}
};

/**
 * Fetches the content's pending suggestions, matched to the latest draft so they list as edit
 * mode does. A request already in flight for the same content is reused, and one for other content
 * is aborted. Successful loads are cached.
 *
 * With `skipDraftWhenEmpty` the draft is only fetched once suggestions are known to exist, so
 * content without any costs one small request. This is for prefetches, which nothing is
 * waiting on. The panel fetches both at once so it opens as fast as possible.
 */
const loadSuggestions = (
	contentId: string,
	contentAri: string,
	{ skipDraftWhenEmpty = false }: { skipDraftWhenEmpty?: boolean } = {},
): Promise<Suggestion[]> => {
	if (inFlightLoad?.contentAri === contentAri) {
		return inFlightLoad.promise;
	}
	inFlightLoad?.controller.abort();

	const controller = new AbortController();
	const { signal } = controller;
	// Without the draft the list cannot be matched, so the unfiltered list is used instead.
	const fetchDraft = () => fetchLatestDraftAdf({ contentId, signal }).catch(() => undefined);
	const promise = skipDraftWhenEmpty
		? fetchPublishedPageSuggestions({ contentAri, signal }).then(async (fetchedSuggestions) =>
				fetchedSuggestions.length === 0
					? fetchedSuggestions
					: matchSuggestionsToDraft(fetchedSuggestions, await fetchDraft()),
			)
		: Promise.all([fetchPublishedPageSuggestions({ contentAri, signal }), fetchDraft()]).then(
				([fetchedSuggestions, latestDraftAdf]) =>
					matchSuggestionsToDraft(fetchedSuggestions, latestDraftAdf),
			);

	const load: SuggestionsLoad = { contentAri, contentId, controller, promise };
	inFlightLoad = load;
	promise.then(
		(suggestions) => {
			// Not cached if it was invalidated or replaced while in flight.
			if (inFlightLoad === load) {
				inFlightLoad = undefined;
				cache = { contentAri, contentId, suggestions, loadedAt: Date.now() };
				notifyListeners();
			}
		},
		() => {
			if (inFlightLoad === load) {
				inFlightLoad = undefined;
			}
		},
	);
	return promise;
};

export const subscribeToPublishedSuggestions = (onStoreChange: () => void): (() => void) => {
	listeners.add(onStoreChange);
	return () => {
		listeners.delete(onStoreChange);
	};
};

export const getPublishedSuggestionsSnapshot = (): PublishedSuggestionsState => snapshot;

export const getInitialPublishedSuggestionsSnapshot = (): PublishedSuggestionsState => initialState;

export const startPublishedSuggestions = (contentId: string, contentAri: string): void => {
	panelLoad = undefined;

	const cachedSuggestions = getFreshCachedSuggestions(contentAri);
	if (cachedSuggestions) {
		setSnapshot({ contentId, status: 'loaded', suggestions: cachedSuggestions });
		return;
	}

	setSnapshot({ contentId, status: 'loading', suggestions: [] });

	const load = loadSuggestions(contentId, contentAri);
	panelLoad = load;
	load.then(
		(suggestions) => {
			if (panelLoad !== load) {
				return;
			}
			panelLoad = undefined;
			setSnapshot({ ...snapshot, status: 'loaded', suggestions });
		},
		() => {
			if (panelLoad !== load) {
				return;
			}
			panelLoad = undefined;
			setSnapshot({ ...snapshot, status: 'error', suggestions: [] });
		},
	);
};

/**
 * Prefetches the content's pending suggestions, so the Review button can show they are new
 * without the panel being opened. Fills the cache the panel reads, so opening it
 * straight after needs no request. Does nothing while fresh suggestions are cached, and shares a
 * request already in flight. Failures are ignored: the panel fetches again when it opens.
 */
export const prefetchPublishedSuggestions = (contentId: string, contentAri: string): void => {
	if (getFreshCachedSuggestions(contentAri)) {
		return;
	}
	loadSuggestions(contentId, contentAri, { skipDraftWhenEmpty: true }).catch(() => {});
};

/**
 * The last suggestions loaded for the content, however old, or `undefined` if none have been. The
 * same array is returned until the cache changes, so it can be read with `useSyncExternalStore`
 * alongside `subscribeToPublishedSuggestions`.
 */
export const getCachedPublishedSuggestions = (contentAri: string): Suggestion[] | undefined =>
	cache?.contentAri === contentAri ? cache.suggestions : undefined;

export const invalidatePublishedSuggestionsCache = (contentId: string): void => {
	if (inFlightLoad?.contentId === contentId) {
		// A request started before the change would cache stale suggestions.
		inFlightLoad = undefined;
	}
	if (cache?.contentId === contentId) {
		cache = undefined;
		notifyListeners();
	}
};

export const selectPublishedSuggestion = (suggestionId: string | undefined): void => {
	if (snapshot.contentId === undefined || snapshot.selectedSuggestionId === suggestionId) {
		return;
	}
	setSnapshot({ ...snapshot, selectedSuggestionId: suggestionId });
};

export const requestPublishedReviewInEditMode = (): void => {
	if (snapshot.contentId === undefined || snapshot.shouldStartReviewInEditMode) {
		return;
	}
	setSnapshot({ ...snapshot, shouldStartReviewInEditMode: true });
};

export const resetPublishedSuggestions = (): void => {
	// The panel stops waiting, but a request in flight is left to finish and be cached so the
	// Review button and the next open can use it.
	panelLoad = undefined;
	if (snapshot !== initialState) {
		setSnapshot(initialState);
	}
};

export const clearPublishedSuggestionsForTesting = (): void => {
	inFlightLoad?.controller.abort();
	inFlightLoad = undefined;
	panelLoad = undefined;
	cache = undefined;
	snapshot = initialState;
	listeners.clear();
};
